// lgpd.jsx
import React from 'react';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';

const PrivacyPolicy = () => (
  <>
  <PageSeo
    path="/lgpd"
    title="Política de Privacidade (LGPD) — Consulta+"
    description="Política de Privacidade da Consulta+ em conformidade com a Lei Geral de Proteção de Dados (Lei 13.709/2018). Como coletamos, usamos, armazenamos e protegemos dados pessoais e de saúde dos usuários da teleconsulta."
    breadcrumbs={[
      { name: 'Início', url: '/' },
      { name: 'Privacidade (LGPD)', url: '/lgpd' },
    ]}
  />
  <LegalPageShell
    eyebrow="Documento legal"
    title="Política de Privacidade e Tratamento de Dados (LGPD)"
    subtitle="Consulta+ — conforme a Lei nº 13.709/2018 · Última atualização: 09/10/2026"
    contactEmail="atendimento@consultamais.org"
  >
    <section>
      <h2>1. Introdução</h2>
      <p>
        Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos os dados pessoais
        dos usuários do aplicativo de teleconsultas “ConsultaMais”. Nosso compromisso é garantir a privacidade
        e a segurança das informações, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018).
      </p>
    </section>

    <section>
      <h2>2. Controlador de Dados</h2>
      <p>
        A “ConsultaMais” é a controladora dos dados pessoais aqui tratados. Para quaisquer dúvidas ou solicitações
        de exercício de direitos, o titular poderá contatar nosso Encarregado de Proteção de Dados (DPO):
      </p>
      <ul>
        <li>E-mail: atendimento@consultamais.org</li>
        <li>Telefone: +55 (41) 98843-0762</li>
      </ul>
    </section>

    <section>
      <h2>3. Dados Coletados</h2>
      <ul>
        <li><strong>Dados de Identificação:</strong> nome completo, CPF, data de nascimento.</li>
        <li><strong>Dados de Contato:</strong> e-mail, telefone.</li>
        <li><strong>Dados de Saúde:</strong> histórico médico, resultados de exames, prescrições, atestados.</li>
        <li><strong>Dados de Pagamento:</strong> registros de transações financeiras (PIX, cartão de crédito).</li>
        <li><strong>Dados de Uso:</strong> logs de acesso, mensagens de chat da teleconsulta e arquivos anexados
            (fotos clínicas, exames). O atendimento é por chat assíncrono; não realizamos nem gravamos chamadas
            de voz ou vídeo.</li>
        <li><strong>Dados de Navegação:</strong> páginas visitadas, cliques, rolagem, tipo de dispositivo e
            navegador e país de acesso, usados para medir e melhorar a usabilidade (seção 10).
            Não incluem conteúdo clínico.</li>
      </ul>
    </section>

    <section>
      <h2>4. Finalidades do Tratamento</h2>
      <p>Tratamos seus dados pessoais para as seguintes finalidades:</p>
      <ul>
        <li>Realizar consultas médicas remotas;</li>
        <li>Elaborar e armazenar prontuários eletrônicos;</li>
        <li>Garantir a continuidade do cuidado entre consultas do mesmo paciente (resumos clínicos internos,
            exibidos ao médico e usados como contexto anonimizado — detalhes em{' '}
            <a href="/transparencia-ia">Transparência sobre IA</a>);</li>
        <li>Emissão e disponibilização de receitas e atestados digitais;</li>
        <li>Gestão de pagamentos e faturamento;</li>
        <li>Melhoria contínua da plataforma, por meio de análises estatísticas e feedback;</li>
        <li>Prevenção a fraudes e uso indevido da plataforma;</li>
        <li>Medição da eficácia dos nossos anúncios on-line (ver item 7);</li>
        <li>Cobrança de valores devidos e exercício regular de direitos, incluindo o envio de notificação
            extrajudicial ao e-mail cadastrado, a resposta a reclamações apresentadas pelo titular em
            canais de consumo e a defesa em processos judiciais, administrativos ou arbitrais;</li>
        <li>Cumprimento de obrigações legais e regulatórias.</li>
      </ul>
    </section>

    <section>
      <h2>5. Bases Legais</h2>
      <ul>
        <li><strong>Tutela da saúde (Art. 11, II, “f”):</strong> base principal para o tratamento de dados
            sensíveis de saúde na teleconsulta, no prontuário e nos fluxos de apoio por IA, realizado sob
            responsabilidade de profissional de saúde.</li>
        <li><strong>Consentimento (Art. 7º I e Art. 11, I):</strong> aceite expresso dos Termos de Uso antes do
            atendimento, registrado com data, IP e hash do conteúdo aceito.</li>
        <li><strong>Execução de Contrato (Art. 7º V):</strong> para prestação de serviço de teleconsulta.</li>
        <li><strong>Obrigação Legal (Art. 7º II):</strong> guarda de prontuários médicos conforme legislação de saúde.</li>
        <li><strong>Legítimo Interesse (Art. 7º IX c/c Art. 10, II):</strong> para prevenção a fraudes e
            segurança da plataforma e para a proteção do exercício regular dos direitos da controladora,
            hipótese que abrange a cobrança de valores devidos e o envio de notificação extrajudicial ao
            titular. Nesses casos são tratados apenas dados cadastrais e da transação (nome, CPF, e-mail,
            telefone, data e valor), sem qualquer informação clínica.</li>
        <li><strong>Legítimo Interesse (Art. 7º IX c/c Art. 10, I e § 1º):</strong> para a análise de
            navegação descrita na seção 10, que busca identificar dificuldades de uso e melhorar a plataforma.
            São tratados apenas os dados de navegação estritamente necessários, vinculados a identificador
            interno, sem dados de saúde, conteúdo do atendimento, CPF, telefone ou e-mail. O titular pode se
            opor a esse tratamento a qualquer momento (art. 18, § 2º).</li>
        <li><strong>Exercício regular de direitos (Art. 7º VI e Art. 11, II, “d”):</strong> quando houver
            processo judicial, administrativo ou arbitral instaurado, inclusive perante órgãos de defesa do
            consumidor, conselhos profissionais ou a ANPD.</li>
      </ul>
    </section>

    <section>
      <h2>6. Retenção de Dados e Prontuários</h2>
      <p>
        Os prontuários eletrônicos e demais dados de saúde serão armazenados, em ambiente criptografado,
        pelo período mínimo de 20 anos, conforme a Lei nº 13.787/2018 e as normas do Conselho Federal de
        Medicina. Após esse prazo, os dados poderão ser anonimizados ou eliminados, salvo se houver
        pendências judiciais ou regulamentares. Pedidos de exclusão são analisados separando o núcleo
        clínico do prontuário (sujeito à guarda legal obrigatória) dos dados cadastrais e acessórios,
        cuja eliminação é atendida quando não houver base legal de conservação (LGPD, arts. 16 e 18).
      </p>
    </section>

    <section>
      <h2>7. Compartilhamento de Dados</h2>
      <p>
        Não vendemos dados pessoais. Compartilhamos dados apenas com <strong>operadores</strong> necessários à
        prestação do serviço, sob contrato e limitados à finalidade específica, e nas hipóteses legais:
      </p>
      <ul>
        <li><strong>Infraestrutura e chat:</strong> Google Cloud/Firebase (hospedagem e banco de dados) e
            Stream (mensagens do chat);</li>
        <li><strong>Apoio de IA:</strong> OpenAI, que recebe apenas o conteúdo clínico necessário,
            <strong> sem nome, CPF, e-mail, telefone ou identificadores de cadastro</strong>, com retenção
            desabilitada em nossas chamadas e sem uso para treinamento — detalhes em{' '}
            <a href="/transparencia-ia">Transparência sobre IA</a>;</li>
        <li><strong>Emissão de documentos:</strong> provedor de assinatura digital ICP-Brasil (Prescreve),
            para receitas, atestados e pedidos de exame;</li>
        <li><strong>Pagamentos:</strong> gateways de pagamento (dados da transação, não do prontuário);</li>
        <li><strong>Comunicações:</strong> provedor de e-mail transacional (endereço e conteúdo da mensagem);</li>
        <li><strong>Medição de anúncios:</strong> quando uma consulta é paga, informamos ao Google Ads que a
            compra ocorreu, com valor, data e o identificador do clique no anúncio, quando houver. E-mail e
            telefone seguem apenas em forma cifrada irreversível (hash SHA-256), que o Google usa somente para
            relacionar a compra ao anúncio visto. <strong>Nenhum dado de saúde é enviado</strong>: queixa, diagnóstico, CID, medicamento e
            documentos ficam fora. Base legal: legítimo interesse (art. 7º, IX). Você pode se opor a esse uso
            escrevendo ao DPO (art. 18, § 2º);</li>
        <li><strong>Análise de navegação:</strong> Hotjar (Hotjar Ltd.), que recebe apenas dados de navegação
            com o conteúdo das telas sensíveis mascarado e nenhum dado de saúde (seção 10);</li>
        <li>Cumprimento de ordem judicial ou requisição de autoridade competente;</li>
        <li>Em situações de risco de vida, quando médicos da equipe poderão compartilhar informações com
            serviço de emergência.</li>
      </ul>
      <p>
        <strong>Transferência internacional:</strong> alguns operadores (ex.: OpenAI, Google e Hotjar) processam
        dados em
        servidores fora do Brasil. A transferência ocorre nos termos do art. 33 da LGPD, com salvaguardas
        contratuais e minimização do conteúdo enviado.
      </p>
    </section>

    <section>
      <h2>8. Direitos dos Titulares</h2>
      <p>
        O titular dos dados possui, a qualquer momento, os seguintes direitos:
      </p>
      <ul>
        <li>Acesso, correção ou exclusão de dados pessoais;</li>
        <li>Retirada de consentimento;</li>
        <li>Portabilidade dos dados a outro fornecedor de serviço;</li>
        <li>Informação sobre eventuais compartilhamentos;</li>
        <li>Revisão de decisões automatizadas — na Consulta+, nenhuma conduta clínica é decidida de forma
            exclusivamente automatizada: o conteúdo clínico gerado com apoio de IA é supervisionado
            (ver <a href="/transparencia-ia">Transparência sobre IA</a>), e o paciente pode ainda assim
            solicitar análise ou contestação de qualquer conduta.</li>
      </ul>
      <p>
        Para exercer seus direitos, escreva para o DPO em atendimento@consultamais.org (sugestão de assunto:
        <strong> [LGPD]</strong>). Responderemos nos prazos da LGPD (até 15 dias corridos — art. 19 §1º II).
      </p>
    </section>

    <section>
      <h2>9. Segurança e Confidencialidade</h2>
      <p>
        Implementamos medidas técnicas e administrativas para proteger os dados contra acesso não autorizado,
        divulgação, alteração e destruição, incluindo:
      </p>
      <ul>
        <li>Criptografia em trânsito (TLS) e em repouso;</li>
        <li>Bloqueio de acesso direto de aplicações cliente ao banco de dados — dados clínicos são acessíveis
            somente pelo backend autenticado;</li>
        <li>Princípio do menor privilégio: credenciais administrativas restritas ao médico responsável,
            armazenadas em gerenciador de segredos e rotacionadas periodicamente;</li>
        <li>Logs de auditoria e monitoramento contínuo, com minimização de dados pessoais nos registros;</li>
        <li>Procedimentos internos escritos de prevenção a vazamento e uso indevido de dados, com auditoria
            periódica, e plano de resposta a incidentes com comunicação à ANPD e aos titulares quando houver
            risco relevante (LGPD, art. 48).</li>
      </ul>
    </section>

    <section>
      <h2>10. Cookies e Tecnologias Semelhantes</h2>
      <p>
        Utilizamos cookies estritamente necessários para funcionamento da plataforma e cookies analíticos para
        melhorar a experiência do usuário. Você pode desativar cookies no seu navegador, mas isso pode afetar
        funcionalidades do site.
      </p>
      <p>
        Também coletamos métricas técnicas e estatísticas pseudonimizadas de uso (rotas visitadas, tempo de
        carregamento, erros, dificuldades de navegação), vinculadas a um identificador interno, para identificar
        gargalos e melhorar a experiência do aplicativo. Esses dados são processados em primeira parte (servidores
        próprios), agregados e descartados após 90 dias. Não associamos essas métricas a CPF, e-mail, telefone ou
        conteúdo de prontuário.
      </p>
      <p>
        <strong>Análise de navegação (Hotjar).</strong> Para entender onde os usuários encontram dificuldade,
        usamos o Hotjar, que reconstrói a navegação na tela (cliques, toques, rolagem e movimentos) e gera mapas
        de calor. A ferramenta foi configurada para preservar o sigilo médico (Código de Ética Médica, art. 73, e
        Resolução CFM nº 2.314/2022) e minimizar dados pessoais (LGPD, art. 6º, III):
      </p>
      <ul>
        <li>o que é digitado em campos de formulário nunca é capturado;</li>
        <li>números e endereços de e-mail exibidos são mascarados em todo o site;</li>
        <li>nas telas de cadastro, triagem, chat da consulta, pagamento, documentos, lembretes de receita e de
            acompanhamento, segunda via e autenticação de documentos, todo o texto exibido é mascarado, e as fotos
            e anexos enviados pelo paciente são substituídos por um bloco neutro;</li>
        <li>as áreas de uso interno da equipe (médicos, suporte e administração) não são gravadas;</li>
        <li>o conteúdo do atendimento, dados de saúde, CPF, telefone, e-mail e nome não são enviados ao Hotjar;</li>
        <li>a sessão é vinculada apenas a um identificador interno aleatório do paciente (pseudônimo) e a marcos
            de navegação, como início do cadastro, abertura da tela de pagamento e confirmação do pagamento, sem o
            conteúdo correspondente;</li>
        <li>o endereço IP é anonimizado pelo Hotjar antes de qualquer armazenamento;</li>
        <li>as gravações ficam armazenadas na União Europeia por até 365 dias, com acesso restrito à equipe
            responsável pela melhoria da plataforma.</li>
      </ul>
      <p>
        O Hotjar utiliza cookies próprios (prefixo <code>_hj</code>). Você pode bloqueá-los nas configurações do
        navegador ou com bloqueadores de rastreamento, sem prejuízo ao atendimento. Para se opor a esse
        tratamento ou pedir a exclusão das gravações vinculadas ao seu cadastro, escreva para o DPO em
        atendimento@consultamais.org (assunto <strong>[LGPD]</strong>).
      </p>
    </section>

    <section>
      <h2>11. Easycheck — verificação B2B de autenticidade de atestados</h2>
      <p>
        A Consulta+ oferece, opcionalmente, o serviço <strong>Easycheck</strong> — relatório formal pago
        (R$ 3,99 por documento) de autenticidade de atestados médicos digitais, destinado a empresas e
        departamentos de recursos humanos que já receberam o atestado de seu colaborador e desejam
        um relatório formal desta plataforma sobre a integridade do arquivo. Detalhes e Termos próprios
        em <a href="/easycheck/termos">consultamais.app/easycheck/termos</a>.
      </p>
      <p>
        <strong>Dados tratados no Easycheck:</strong>
      </p>
      <ul>
        <li><strong>Da empresa contratante:</strong> razão social, CNPJ, e-mail corporativo (informados pela própria empresa no formulário).</li>
        <li><strong>Do colaborador (titular do atestado):</strong> conteúdo do PDF que já está em posse da empresa — nome, CPF, texto do atestado. A Consulta+ NÃO revela à empresa dados clínicos adicionais que não constem do próprio documento apresentado (CID, diagnóstico, medicamentos ou histórico não são compartilhados).</li>
      </ul>
      <p>
        <strong>Base legal do tratamento:</strong>
      </p>
      <ul>
        <li>Art. 7º V da LGPD — execução do contrato do serviço Easycheck com a empresa contratante.</li>
        <li>Art. 7º IX da LGPD — legítimo interesse da empresa em verificar a autenticidade de documento que já lhe foi apresentado pelo colaborador (relação de emprego ou prestação de serviços).</li>
        <li>Art. 11 II &quot;a&quot; e &quot;f&quot; da LGPD — tratamento de dado sensível de saúde para tutela da saúde e prevenção a fraude, realizado exclusivamente por profissionais e sistemas da área da saúde.</li>
      </ul>
      <p>
        <strong>Escopo restrito e finalidade específica:</strong> a análise se limita à autenticidade e
        integridade do arquivo enviado, mediante comparação criptográfica com originais registrados
        pela plataforma. Não há qualquer uso do documento para finalidade diversa (pesquisa,
        treinamento de IA, publicidade etc.).
      </p>
      <p>
        <strong>Retenção:</strong> o arquivo PDF enviado pela empresa e os metadados do pedido são
        armazenados de forma criptografada por até <strong>90 dias</strong> após a emissão do
        resultado, exclusivamente para auditoria, atendimento a solicitações legítimas (do titular
        ou de autoridades) e prevenção a fraude. Após esse prazo, o PDF é excluído automaticamente
        do bucket. Metadados sem conteúdo clínico (identificadores da transação, hash, resultado)
        podem ser mantidos por período maior para cumprimento de obrigação fiscal e trilha de
        auditoria.
      </p>
      <p>
        <strong>Não compartilhamento:</strong> os documentos submetidos ao Easycheck e seus resultados
        NÃO são compartilhados com terceiros, ressalvadas as hipóteses de ordem judicial, requisição
        de autoridade competente ou obrigação legal.
      </p>
      <p>
        <strong>Direitos do colaborador (titular):</strong> mesmo sem ser o contratante direto do serviço,
        o colaborador cujo atestado foi verificado permanece titular dos dados pessoais tratados e
        pode exercer, a qualquer tempo, todos os direitos previstos no art. 18 da LGPD pelo canal
        atendimento@consultamais.org (Encarregado / Diretor Técnico).
      </p>
      <p>
        <strong>Caminho gratuito sempre disponível:</strong> a validação oficial de assinatura ICP-Brasil
        continua disponível gratuitamente no portal do ITI em <a href="https://validar.iti.gov.br" target="_blank" rel="noopener noreferrer">validar.iti.gov.br</a>{' '}
        (tutorial em <a href="/autenticar">consultamais.app/autenticar</a>). O Easycheck é serviço
        opcional de conveniência, não substitui nem invalida o canal público gratuito.
      </p>
    </section>

    <section>
      <h2>12. Alterações nesta Política</h2>
      <p>
        Podemos atualizar esta Política de Privacidade periodicamente. Avisos sobre mudanças substanciais
        serão divulgados em nosso site e/ou via e-mail.
      </p>
    </section>

    <section>
      <h2>13. Responsável técnico e transparência</h2>
      <p>
        O Diretor Técnico e médico responsável pelos atendimentos da Consulta+ é o{' '}
        <a href="/diretor"><strong>Dr. Guilherme Esmanhotto — CRM 23.706/DF</strong></a>,
        formado em Medicina pela Universidade Federal do Paraná (UFPR). Toda a operação clínica é
        conduzida sob sua supervisão, em conformidade com a{' '}
        <strong>Resolução CFM 2.314/2022</strong> (telemedicina) e com a{' '}
        <strong>Resolução CFM 2.454/2026</strong> (uso de IA na prática médica).
      </p>
      <p>
        Documento público sobre como a Inteligência Artificial é empregada nos atendimentos,
        sob supervisão humana obrigatória: <a href="/transparencia-ia">Transparência sobre IA</a>.
      </p>
      <p>
        Receitas, atestados e pedidos de exame são entregues em PDF, com assinatura digital{' '}
        <strong>ICP-Brasil</strong>, válidos em todo o Brasil. A autenticidade pode ser verificada
        em <a href="/autenticar">consultamais.app/autenticar</a>.
      </p>
    </section>

    <section>
      <h2>14. Contato</h2>
      <p>
        Em caso de dúvidas ou solicitações sobre esta Política, entre em contato com o
        Encarregado / Diretor Técnico:
      </p>
      <ul>
        <li>E-mail: atendimento@consultamais.org</li>
        <li>Telefone: +55 (41) 98843-0762</li>
      </ul>
    </section>
  </LegalPageShell>
  </>
);

export default PrivacyPolicy;
